import React, { useState, useEffect, useRef, useCallback } from 'react';
import UMapsWordmark from './components/UMapsWordmark.jsx';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_MAPS_KEY;
const CENTER = { lat: 49.80939, lng: -97.13435 };
const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';

const CATEGORY_OPTIONS = [
  'Visitor – Pay-by-Plate',
  'Student Permit – Permit',
  'Staff Permit – Permit',
  'Accessible Parking',
  'Residence Parking',
  'Pick-up and Drop-off Zone',
  'After-Hours / Parkade',
  'Permit Only',
];

const CAT_COLORS = {
  residence: { fill: '#a1887f', stroke: '#795548', labelBg: 'rgba(200,170,155,0.95)' },
  visitor:   { fill: '#1565c0', stroke: '#0d47a1', labelBg: 'rgba(140,190,240,0.95)' },
  student:   { fill: '#f57c00', stroke: '#e65100', labelBg: 'rgba(255,185,100,0.95)' },
  staff:     { fill: '#5d4037', stroke: '#4e342e', labelBg: 'rgba(160,120,100,0.95)' },
  dropoff:   { fill: '#00897b', stroke: '#00695c', labelBg: 'rgba(100,210,200,0.95)' },
  default:   { fill: '#f4a900', stroke: '#e07b00', labelBg: 'rgba(255,195,80,0.95)' },
};

function catColors(category) {
  const cat = (category || '').toLowerCase();
  if (cat.includes('drop'))      return CAT_COLORS.dropoff;
  if (cat.includes('residence')) return CAT_COLORS.residence;
  if (cat.includes('visitor') || cat.includes('parkade') || cat.includes('after-hours')) return CAT_COLORS.visitor;
  if (cat.includes('student'))   return CAT_COLORS.student;
  if (cat.includes('staff'))     return CAT_COLORS.staff;
  return CAT_COLORS.default;
}

function isPaidNow(paidSchedule) {
  if (!paidSchedule) return false;
  const now = new Date();
  const day = now.getDay();
  const min = now.getHours() * 60 + now.getMinutes();
  if (paidSchedule.type === 'weekday_window') {
    if (day === 0 || day === 6) return false;
    return min >= 7 * 60 + 30 && min < 16 * 60 + 30;
  }
  if (paidSchedule.type === 'after_hours') {
    if (day === 0 || day === 6) return true;
    return min >= 16 * 60 + 30 || min < 7 * 60 + 30;
  }
  if (paidSchedule.type === 'parkade') return true;
  return false;
}

function formatSchedule(paidSchedule) {
  if (!paidSchedule) return '—';
  if (paidSchedule.type === 'weekday_window') return 'Mon–Fri, 7:30 AM – 4:30 PM';
  if (paidSchedule.type === 'after_hours')    return 'Mon–Fri 4:30 PM – 7:30 AM / Sat–Sun all day';
  if (paidSchedule.type === 'parkade')        return 'Daily 6:00 AM – 6:00 PM / 6:00 PM – 6:00 AM';
  if (paidSchedule.type === 'permit_only')    return 'Permit required (no visitor pay)';
  return '—';
}

function loadGoogleMapsScript(apiKey) {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) { resolve(); return; }
    const existing = document.getElementById('gmap-script');
    if (existing) { existing.addEventListener('load', resolve); return; }
    const script = document.createElement('script');
    script.id = 'gmap-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true; script.defer = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
}

const EMPTY_DRAFT = {
  name: '', category: '', hourlyRate: '', timeLimit: '',
  dailyMax: '', zone: '', payLink: '', description: '',
  paidScheduleType: '',
};

export default function ParkingCreator() {
  // ── Auth gate ─────────────────────────────────────────────────────────────────
  const [authed,      setAuthed]      = useState(() => sessionStorage.getItem('pcAuth') === '1');
  const [pwInput,     setPwInput]     = useState('');
  const [pwError,     setPwError]     = useState('');
  const [pwLoading,   setPwLoading]   = useState(false);

  const mapRef          = useRef(null);
  const mapInstanceRef  = useRef(null);
  const overlayClassRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  // Flat parking entry list
  const parkingLotsRef = useRef([]);       // source of truth
  const [parkingLots, setParkingLots] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saveStatus,  setSaveStatus]  = useState(null);

  // Drawing state
  const parkingDrawPointsRef    = useRef([]);
  const parkingPolyRef          = useRef(null);
  const parkingPreviewLineRef   = useRef(null);
  const parkingCloseMarkerRef   = useRef(null);
  const parkingExistingPolyRef  = useRef(null);
  const parkingClickListenerRef = useRef(null);
  const parkingMouseListenerRef = useRef(null);
  const handleParkingCloseRef   = useRef(null);
  const exitMarkersRef          = useRef([]);
  const parkingSubModeRef       = useRef('polygon');
  const selectedEntryRef        = useRef(null);        // entry name string
  const allLotsPolysRef         = useRef([]);          // { poly, label }[] for all entries

  const [parkingPointCount, setParkingPointCount] = useState(0);
  const [parkingSubMode,    setParkingSubMode]    = useState('polygon');
  const [exitPointCount,    setExitPointCount]    = useState(0);
  const [selectedEntry,     setSelectedEntry]     = useState(null);
  const [drawActive,        setDrawActive]        = useState(false);
  const [entryNameDraft,    setEntryNameDraft]    = useState('');

  // Form for adding a new entry
  const [newFormOpen, setNewFormOpen] = useState(false);
  const [newDraft,    setNewDraft]    = useState(EMPTY_DRAFT);

  // Test mode
  const [testMode,     setTestMode]     = useState(false);
  const [testData,     setTestData]     = useState([]);
  const [testInfoData, setTestInfoData] = useState(null);
  const testFileInputRef  = useRef(null);
  const testPolyItemsRef  = useRef([]);

  // Sync ref
  useEffect(() => { parkingSubModeRef.current = parkingSubMode; }, [parkingSubMode]);

  // ── Init map + fetch ─────────────────────────────────────────────────────────
  useEffect(() => {
    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY).then(() => {
      if (!mapRef.current) return;
      const map = new window.google.maps.Map(mapRef.current, {
        center: CENTER, zoom: 17,
        mapTypeId: 'satellite', tilt: 0,
        disableDefaultUI: true, zoomControl: true, gestureHandling: 'greedy',
      });
      mapInstanceRef.current = map;

      // OverlayView for polygon centre labels
      class ParkingLabelOverlay extends window.google.maps.OverlayView {
        constructor(position, html, bgColor) {
          super();
          this.position = position; this.html = html; this.bgColor = bgColor; this._div = null;
        }
        onAdd() {
          const div = document.createElement('div');
          div.style.cssText = [
            'position:absolute', `background:${this.bgColor}`,
            'border-radius:5px', 'padding:3px 8px',
            'font-family:Arial,sans-serif', 'font-size:11px', 'font-weight:700',
            'color:#111', 'pointer-events:none', 'transform:translate(-50%,-50%)',
            'box-shadow:0 1px 5px rgba(0,0,0,0.22)', 'white-space:nowrap',
            'border:1px solid rgba(0,0,0,0.12)', 'text-align:center', 'line-height:1.3',
          ].join(';');
          div.innerHTML = this.html;
          this._div = div;
          this.getPanes().floatPane.appendChild(div);
        }
        draw() {
          const pos = this.getProjection().fromLatLngToDivPixel(this.position);
          if (pos && this._div) { this._div.style.left = pos.x + 'px'; this._div.style.top = pos.y + 'px'; }
        }
        onRemove() {
          if (this._div) { this._div.parentNode?.removeChild(this._div); this._div = null; }
        }
      }
      overlayClassRef.current = ParkingLabelOverlay;
      setMapReady(true);
    });

    fetch(`${API_BASE}/api/parking`)
      .then(r => r.json())
      .then(data => {
        const lots = Array.isArray(data) ? data : [];
        parkingLotsRef.current = lots;
        setParkingLots(lots);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // ── Draw helpers ─────────────────────────────────────────────────────────────
  const clearParkingDraw = useCallback(() => {
    if (parkingPolyRef.current)        { parkingPolyRef.current.setMap(null);        parkingPolyRef.current = null; }
    if (parkingPreviewLineRef.current) { parkingPreviewLineRef.current.setMap(null); parkingPreviewLineRef.current = null; }
    if (parkingCloseMarkerRef.current) { parkingCloseMarkerRef.current.setMap(null); parkingCloseMarkerRef.current = null; }
    if (parkingMouseListenerRef.current) {
      window.google.maps.event.removeListener(parkingMouseListenerRef.current);
      parkingMouseListenerRef.current = null;
    }
  }, []);

  // Render all lot polygons with centre labels
  const renderAllLots = useCallback((lots, map) => {
    allLotsPolysRef.current.forEach(({ poly, label }) => {
      poly.setMap(null);
      if (label) label.setMap(null);
    });
    allLotsPolysRef.current = [];
    const OClass = overlayClassRef.current;
    if (!map || !OClass) return;

    lots.forEach(entry => {
      if (!entry.polygon || entry.polygon.length < 3) return;
      const colors = catColors(entry.category);
      const poly = new window.google.maps.Polygon({
        paths: entry.polygon.map(c => ({ lat: c.latitude, lng: c.longitude })),
        strokeColor: colors.stroke, strokeOpacity: 0.8, strokeWeight: 1.5,
        fillColor: colors.fill, fillOpacity: 0.18, zIndex: 48, clickable: false, map,
      });

      // Centroid label
      const n = entry.polygon.length;
      const lat = entry.polygon.reduce((s, c) => s + c.latitude,  0) / n;
      const lng = entry.polygon.reduce((s, c) => s + c.longitude, 0) / n;
      const label = new OClass(
        new window.google.maps.LatLng(lat, lng),
        // Escaped: the overlay writes this with innerHTML (the test labels below do the same).
        `<span>${String(entry.name || '?').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>`,
        colors.labelBg,
      );
      label.setMap(map);
      allLotsPolysRef.current.push({ poly, label });
    });
  }, []);

  useEffect(() => {
    if (mapReady) renderAllLots(parkingLots, mapInstanceRef.current);
  }, [parkingLots, mapReady, renderAllLots]);

  const updateParkingPreview = useCallback(map => {
    const pts = parkingDrawPointsRef.current;
    if (!parkingPolyRef.current) {
      parkingPolyRef.current = new window.google.maps.Polygon({
        paths: pts, strokeColor: '#f4a900', strokeOpacity: 0.9, strokeWeight: 2,
        fillColor: '#f4a900', fillOpacity: 0.2, zIndex: 100, clickable: false, map,
      });
    } else {
      parkingPolyRef.current.setPaths(pts);
    }
    if (pts.length >= 3 && !parkingCloseMarkerRef.current) {
      parkingCloseMarkerRef.current = new window.google.maps.Marker({
        position: { lat: pts[0].lat, lng: pts[0].lng },
        map, zIndex: 200, clickable: false,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: '#f4a900', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2.5 },
        title: 'Click to close polygon',
      });
    }
  }, []);

  // Select an entry by name → show existing polygon + exits and activate draw mode
  const selectEntry = useCallback((entryName) => {
    setParkingSubMode('polygon');
    parkingSubModeRef.current = 'polygon';
    clearParkingDraw();
    parkingDrawPointsRef.current = [];
    setParkingPointCount(0);
    if (parkingExistingPolyRef.current) { parkingExistingPolyRef.current.setMap(null); parkingExistingPolyRef.current = null; }
    exitMarkersRef.current.forEach(m => m.setMap(null));
    exitMarkersRef.current = [];

    const entry = parkingLotsRef.current.find(e => e.name === entryName);

    if (entry?.polygon?.length > 0) {
      const paths = entry.polygon.map(c => ({ lat: c.latitude, lng: c.longitude }));
      parkingExistingPolyRef.current = new window.google.maps.Polygon({
        paths, strokeColor: '#1a73e8', strokeOpacity: 0.7, strokeWeight: 2,
        fillColor: '#1a73e8', fillOpacity: 0.12, zIndex: 98, clickable: false,
        map: mapInstanceRef.current,
      });
      // Zoom to polygon
      const bounds = new window.google.maps.LatLngBounds();
      entry.polygon.forEach(c => bounds.extend({ lat: c.latitude, lng: c.longitude }));
      mapInstanceRef.current?.fitBounds(bounds, { top: 90, right: 30, bottom: 80, left: 30 });
    }

    setEntryNameDraft(entryName);

    const exits = entry?.exits || [];
    setExitPointCount(exits.length);
    const map = mapInstanceRef.current;
    if (map) {
      exits.forEach(ex => {
        const m = new window.google.maps.Marker({
          position: { lat: ex.latitude, lng: ex.longitude },
          map, zIndex: 300, clickable: false,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: '#ff5722', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
        });
        exitMarkersRef.current.push(m);
      });
    }

    selectedEntryRef.current = entryName;
    setSelectedEntry(entryName);
    setDrawActive(true);
  }, [clearParkingDraw]);

  // ── Click / mousemove listeners ──────────────────────────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady || !drawActive) {
      if (parkingClickListenerRef.current) { window.google.maps.event.removeListener(parkingClickListenerRef.current); parkingClickListenerRef.current = null; }
      if (parkingMouseListenerRef.current) { window.google.maps.event.removeListener(parkingMouseListenerRef.current); parkingMouseListenerRef.current = null; }
      return;
    }

    parkingClickListenerRef.current = map.addListener('click', e => {
      if (parkingSubModeRef.current === 'exits') {
        const pt = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        const entryName = selectedEntryRef.current;
        if (!entryName) return;
        const updated = parkingLotsRef.current.map(en => en.name !== entryName ? en : {
          ...en, exits: [...(en.exits || []), { latitude: pt.lat, longitude: pt.lng }],
        });
        parkingLotsRef.current = updated;
        setParkingLots(updated);
        const marker = new window.google.maps.Marker({
          position: pt, map, zIndex: 300, clickable: false,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: '#ff5722', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
        });
        exitMarkersRef.current.push(marker);
        setExitPointCount(prev => prev + 1);
        return;
      }

      // Polygon draw mode
      const pts = parkingDrawPointsRef.current;
      if (pts.length >= 3) {
        const proj  = map.getProjection();
        const scale = Math.pow(2, map.getZoom());
        const fp    = proj.fromLatLngToPoint(new window.google.maps.LatLng(pts[0].lat, pts[0].lng));
        const cp    = proj.fromLatLngToPoint(e.latLng);
        const dx    = (cp.x - fp.x) * scale;
        const dy    = (cp.y - fp.y) * scale;
        if (Math.sqrt(dx * dx + dy * dy) < 20) { handleParkingCloseRef.current?.(); return; }
      }
      const pt = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      parkingDrawPointsRef.current = [...pts, pt];
      setParkingPointCount(parkingDrawPointsRef.current.length);
      updateParkingPreview(map);
    });

    parkingMouseListenerRef.current = map.addListener('mousemove', e => {
      const pts = parkingDrawPointsRef.current;
      if (pts.length === 0 || parkingSubModeRef.current !== 'polygon') return;
      const mouse = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      const last  = pts[pts.length - 1];
      if (!parkingPreviewLineRef.current) {
        parkingPreviewLineRef.current = new window.google.maps.Polyline({
          path: [last, mouse], strokeColor: '#f4a900',
          strokeOpacity: 0.5, strokeWeight: 2, zIndex: 101, clickable: false, map,
        });
      } else {
        parkingPreviewLineRef.current.setPath([last, mouse]);
      }
    });

    return () => {
      if (parkingClickListenerRef.current) { window.google.maps.event.removeListener(parkingClickListenerRef.current); parkingClickListenerRef.current = null; }
      if (parkingMouseListenerRef.current) { window.google.maps.event.removeListener(parkingMouseListenerRef.current); parkingMouseListenerRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawActive, mapReady]);

  const handleParkingUndo = () => {
    if (parkingDrawPointsRef.current.length === 0) return;
    parkingDrawPointsRef.current = parkingDrawPointsRef.current.slice(0, -1);
    setParkingPointCount(parkingDrawPointsRef.current.length);
    const map = mapInstanceRef.current;
    if (parkingDrawPointsRef.current.length > 0) {
      updateParkingPreview(map);
    } else if (parkingPolyRef.current) {
      parkingPolyRef.current.setMap(null); parkingPolyRef.current = null;
    }
    if (parkingDrawPointsRef.current.length < 3 && parkingCloseMarkerRef.current) {
      parkingCloseMarkerRef.current.setMap(null); parkingCloseMarkerRef.current = null;
    }
  };

  const handleParkingClose = () => {
    const pts       = parkingDrawPointsRef.current;
    if (pts.length < 3) return;
    const coords    = pts.map(p => ({ latitude: p.lat, longitude: p.lng }));
    const entryName = selectedEntryRef.current;
    const updated   = parkingLotsRef.current.map(en =>
      en.name !== entryName ? en : { ...en, polygon: coords }
    );
    setParkingLots(updated);
    parkingLotsRef.current = updated;
    clearParkingDraw();
    parkingDrawPointsRef.current = [];
    setParkingPointCount(0);
    selectEntry(entryName);
  };
  handleParkingCloseRef.current = handleParkingClose;

  const handleUndoExit = () => {
    const entryName = selectedEntryRef.current;
    if (!entryName) return;
    const updated = parkingLotsRef.current.map(en => en.name !== entryName ? en : {
      ...en, exits: (en.exits || []).slice(0, -1),
    });
    parkingLotsRef.current = updated;
    setParkingLots(updated);
    const last = exitMarkersRef.current.pop();
    if (last) last.setMap(null);
    setExitPointCount(prev => Math.max(0, prev - 1));
  };

  const handleDeselect = useCallback(() => {
    clearParkingDraw();
    exitMarkersRef.current.forEach(m => m.setMap(null));
    exitMarkersRef.current = [];
    setExitPointCount(0);
    if (parkingExistingPolyRef.current) { parkingExistingPolyRef.current.setMap(null); parkingExistingPolyRef.current = null; }
    parkingDrawPointsRef.current = [];
    setParkingPointCount(0);
    setParkingSubMode('polygon');
    parkingSubModeRef.current = 'polygon';
    selectedEntryRef.current = null;
    setSelectedEntry(null);
    setDrawActive(false);
  }, [clearParkingDraw]);

  // ── Entry management ─────────────────────────────────────────────────────────
  const handleDeleteEntry = useCallback((entryName) => {
    if (!window.confirm(`Delete "${entryName}"? This cannot be undone.`)) return;
    handleDeselect();
    const updated = parkingLotsRef.current.filter(en => en.name !== entryName);
    parkingLotsRef.current = updated;
    setParkingLots(updated);
  }, [handleDeselect]);

  const handleRenameEntry = useCallback((oldName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;
    const updated = parkingLotsRef.current.map(en =>
      en.name !== oldName ? en : { ...en, name: trimmed }
    );
    parkingLotsRef.current = updated;
    setParkingLots(updated);
    selectedEntryRef.current = trimmed;
    setSelectedEntry(trimmed);
    setEntryNameDraft(trimmed);
  }, []);

  const handleClearPolygon = useCallback(() => {
    const entryName = selectedEntryRef.current;
    if (!entryName) return;
    const updated = parkingLotsRef.current.map(en =>
      en.name !== entryName ? en : { ...en, polygon: [] }
    );
    parkingLotsRef.current = updated;
    setParkingLots(updated);
    if (parkingExistingPolyRef.current) { parkingExistingPolyRef.current.setMap(null); parkingExistingPolyRef.current = null; }
    clearParkingDraw();
    parkingDrawPointsRef.current = [];
    setParkingPointCount(0);
  }, [clearParkingDraw]);

  const handleClearExits = useCallback(() => {
    const entryName = selectedEntryRef.current;
    if (!entryName) return;
    const updated = parkingLotsRef.current.map(en =>
      en.name !== entryName ? en : { ...en, exits: [] }
    );
    parkingLotsRef.current = updated;
    setParkingLots(updated);
    exitMarkersRef.current.forEach(m => m.setMap(null));
    exitMarkersRef.current = [];
    setExitPointCount(0);
  }, []);

  const handleUpdateField = useCallback((entryName, field, value) => {
    const updated = parkingLotsRef.current.map(en =>
      en.name !== entryName ? en : { ...en, [field]: value || null }
    );
    parkingLotsRef.current = updated;
    setParkingLots(updated);
  }, []);

  const handleUpdatePaidSchedule = useCallback((entryName, scheduleType) => {
    const updated = parkingLotsRef.current.map(en =>
      en.name !== entryName ? en : {
        ...en, paidSchedule: scheduleType ? { type: scheduleType } : null,
      }
    );
    parkingLotsRef.current = updated;
    setParkingLots(updated);
  }, []);

  // Add new parking lot entry
  const handleAddEntry = () => {
    const name = newDraft.name.trim();
    if (!name) return;
    const entry = {
      name,
      category:    newDraft.category || null,
      hourlyRate:  newDraft.hourlyRate || null,
      timeLimit:   newDraft.timeLimit || null,
      dailyMax:    newDraft.dailyMax || null,
      zone:        newDraft.zone || null,
      payLink:     newDraft.payLink || null,
      description: newDraft.description || '',
      paidSchedule: newDraft.paidScheduleType ? { type: newDraft.paidScheduleType } : null,
      polygon:     [],
      exits:       [],
    };
    const updated = [...parkingLotsRef.current, entry];
    parkingLotsRef.current = updated;
    setParkingLots(updated);
    setNewDraft(EMPTY_DRAFT);
    setNewFormOpen(false);
    selectEntry(name);
  };

  // ── Test mode ────────────────────────────────────────────────────────────────
  const renderTestPolygons = useCallback((lots) => {
    const map = mapInstanceRef.current;
    const OClass = overlayClassRef.current;
    if (!map || !OClass) return;
    testPolyItemsRef.current.forEach(({ poly, label }) => { poly.setMap(null); label.setMap(null); });
    testPolyItemsRef.current = [];
    const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    lots.forEach(entry => {
      if (!entry.polygon || entry.polygon.length < 3) return;
      const colors = catColors(entry.category);
      const paths  = entry.polygon.map(c => ({ lat: c.latitude, lng: c.longitude }));
      const poly   = new window.google.maps.Polygon({
        paths, strokeColor: colors.stroke, strokeOpacity: 0.8, strokeWeight: 1.5,
        fillColor: colors.fill, fillOpacity: 0.25, zIndex: 50, clickable: true, map,
      });
      poly.addListener('click',     () => setTestInfoData(entry));
      poly.addListener('mouseover', () => poly.setOptions({ fillOpacity: 0.45, strokeOpacity: 1 }));
      poly.addListener('mouseout',  () => poly.setOptions({ fillOpacity: 0.25, strokeOpacity: 0.8 }));

      const n   = entry.polygon.length;
      const lat = entry.polygon.reduce((s, c) => s + c.latitude,  0) / n;
      const lng = entry.polygon.reduce((s, c) => s + c.longitude, 0) / n;
      const paid    = isPaidNow(entry.paidSchedule);
      const isPermit = entry.paidSchedule?.type === 'permit_only';
      let subHtml = '';
      if (!isPermit) {
        subHtml = paid
          ? `<div style="font-size:10px;color:#c62828;font-weight:700">PAYING NOW</div>`
          : `<div style="font-size:10px;color:#2e7d32;font-weight:700">FREE NOW</div>`;
      }
      const html = `<div style="text-align:center;line-height:1.5"><div style="font-weight:700;font-size:12px">${esc(entry.name)}</div>${subHtml}</div>`;
      const label = new OClass(new window.google.maps.LatLng(lat, lng), html, colors.labelBg);
      label.setMap(map);
      testPolyItemsRef.current.push({ poly, label });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exitTestMode = useCallback(() => {
    testPolyItemsRef.current.forEach(({ poly, label }) => { poly.setMap(null); label.setMap(null); });
    testPolyItemsRef.current = [];
    setTestMode(false); setTestData([]); setTestInfoData(null);
  }, []);

  const handleTestFile = useCallback(e => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const data = JSON.parse(ev.target.result);
        const lots = Array.isArray(data) ? data : [];
        handleDeselect();
        setTestData(lots); setTestMode(true);
        if (mapReady) renderTestPolygons(lots);
      } catch { alert('Invalid JSON — could not parse parking_lots.json'); }
    };
    reader.readAsText(file);
  }, [mapReady, renderTestPolygons, handleDeselect]);

  // ── Save / download ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      const res  = await fetch(`${API_BASE}/api/save-parking`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parkingLotsRef.current),
      });
      const json = await res.json();
      if (json.ok) { setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 3000); }
      else          setSaveStatus('error');
    } catch { setSaveStatus('error'); }
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(parkingLotsRef.current, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = 'parking_lots.json'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Auth gate render ──────────────────────────────────────────────────────────
  if (!authed) {
    const handleLogin = async (e) => {
      e.preventDefault();
      setPwLoading(true); setPwError('');
      try {
        const res = await fetch(`${API_BASE}/api/auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: pwInput }),
        });
        if (res.ok) {
          sessionStorage.setItem('pcAuth', '1');
          setAuthed(true);
        } else {
          const data = await res.json().catch(() => ({}));
          setPwError(data.error || 'Incorrect password.');
        }
      } catch {
        setPwError('Network error. Try again.');
      }
      setPwLoading(false);
    };
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9' }}>
        <form onSubmit={handleLogin} style={{ background: '#fff', borderRadius: 16, padding: '36px 32px', boxShadow: '0 8px 32px rgba(0,0,0,0.14)', width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <img src="/UMaps Logo Circle Border.png" alt="UMaps" style={{ width: 52, height: 52, borderRadius: '50%', marginBottom: 10 }} onError={e => e.target.style.display = 'none'} />
            <div style={{ fontSize: 20, fontWeight: 600, color: '#0f172a' }}><b style={{ fontWeight: 800 }}>UMaps</b> Parking Creator</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Enter the password to continue</div>
          </div>
          <input
            autoFocus
            type="password"
            placeholder="Password"
            value={pwInput}
            onChange={e => { setPwInput(e.target.value); setPwError(''); }}
            style={{ padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${pwError ? '#f87171' : '#e2e8f0'}`, fontSize: 14, outline: 'none' }}
          />
          {pwError && <div style={{ fontSize: 12, color: '#dc2626', marginTop: -8 }}>{pwError}</div>}
          <button type="submit" disabled={pwLoading || !pwInput} style={{ padding: '12px', borderRadius: 10, border: 'none', background: '#d94f4f', color: '#fff', fontSize: 14, fontWeight: 700, cursor: pwLoading ? 'default' : 'pointer', opacity: !pwInput ? 0.6 : 1 }}>
            {pwLoading ? 'Checking…' : 'Enter'}
          </button>
        </form>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  const inputStyle = { width: '100%', boxSizing: 'border-box', fontSize: 10, padding: '4px 7px', border: '1px solid #e0e0e0', borderRadius: 4, outline: 'none', background: '#fff' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'system-ui, sans-serif', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', height: 52, background: testMode ? '#0d2137' : '#1a1a2e', color: '#fff', flexShrink: 0, gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <UMapsWordmark tool="Parking Creator" size={24} href={null} style={{ fontSize: 17 }} />
          {testMode
            ? <span style={{ fontSize: 11, fontWeight: 700, color: '#fbbf24', background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.35)', borderRadius: 5, padding: '2px 9px' }}>TEST MODE</span>
            : <span style={{ fontSize: 11, color: '#8888aa' }}>myumaps.ca/parkingcreator</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {testMode ? (
            <>
              <span style={{ fontSize: 12, color: '#aaa' }}>{testData.filter(e => e.polygon?.length >= 3).length} polygons</span>
              <button onClick={exitTestMode} style={{ fontSize: 12, fontWeight: 700, padding: '6px 16px', borderRadius: 6, border: '1px solid #ef5350', background: 'rgba(239,83,80,0.15)', color: '#ef9a9a', cursor: 'pointer' }}>✕ Exit Test</button>
            </>
          ) : (
            <>
              {saveStatus === 'saved'  && <span style={{ fontSize: 12, color: '#4caf50' }}>✓ Saved</span>}
              {saveStatus === 'error'  && <span style={{ fontSize: 12, color: '#ef5350' }}>✗ Failed — use Download</span>}
              {saveStatus === 'saving' && <span style={{ fontSize: 12, color: '#aaa' }}>Saving…</span>}
              <button onClick={handleSave} disabled={saveStatus === 'saving'}
                style={{ fontSize: 12, fontWeight: 600, padding: '6px 16px', borderRadius: 6, border: 'none', background: '#34a853', color: '#fff', cursor: 'pointer' }}>↑ Save</button>
              <button onClick={handleDownload}
                style={{ fontSize: 12, fontWeight: 600, padding: '6px 16px', borderRadius: 6, border: '1px solid #555', background: 'none', color: '#ccc', cursor: 'pointer' }}>↓ Download</button>
              <button onClick={() => testFileInputRef.current?.click()}
                style={{ fontSize: 12, fontWeight: 600, padding: '6px 16px', borderRadius: 6, border: '1px solid #7c6fcf', background: 'rgba(124,111,207,0.15)', color: '#b4a8f5', cursor: 'pointer' }}>🧪 Test File</button>
              <input ref={testFileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleTestFile} />
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <div style={{ width: 270, display: 'flex', flexDirection: 'column', background: '#fff', borderRight: '1px solid #e0e0e0', overflowY: 'auto', flexShrink: 0 }}>

          {/* Test mode banner */}
          {testMode && (
            <div style={{ padding: '16px 14px', background: '#fffbea', borderBottom: '1px solid #f0d060', flexShrink: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#b45309', marginBottom: 8 }}>TEST MODE — READ ONLY</div>
              <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
                <b>{testData.length}</b> lots · <b>{testData.filter(e => e.polygon?.length >= 3).length}</b> polygons
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>Click any polygon to view info.</div>
              <button onClick={exitTestMode} style={{ marginTop: 10, width: '100%', fontSize: 12, fontWeight: 700, padding: '6px 0', borderRadius: 5, border: '1px solid #ef5350', background: '#fff3f0', color: '#c62828', cursor: 'pointer' }}>✕ Exit Test</button>
            </div>
          )}

          {/* Lot list header */}
          {!testMode && (
            <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#888' }}>PARKING LOTS ({parkingLots.length})</span>
              <button onClick={() => setNewFormOpen(v => !v)}
                style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 4, border: `1px solid ${newFormOpen ? '#e53935' : '#34a853'}`, background: newFormOpen ? '#fff3f0' : '#e8f5e9', color: newFormOpen ? '#e53935' : '#2e7d32', cursor: 'pointer' }}>
                {newFormOpen ? '✕ Cancel' : '+ New Lot'}
              </button>
            </div>
          )}

          {/* New lot form */}
          {!testMode && newFormOpen && (
            <div style={{ padding: '10px 14px', background: '#f0fff4', borderBottom: '1px solid #c8e6c9', flexShrink: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#2e7d32', marginBottom: 8 }}>NEW PARKING LOT</div>

              <div style={{ marginBottom: 5 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#777', marginBottom: 1 }}>Name *</div>
                <input type="text" value={newDraft.name} placeholder="e.g. Lot A – Visitor"
                  onChange={e => setNewDraft(d => ({ ...d, name: e.target.value }))}
                  style={{ ...inputStyle }} />
              </div>

              <div style={{ marginBottom: 5 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#777', marginBottom: 1 }}>Category</div>
                <select value={newDraft.category} onChange={e => setNewDraft(d => ({ ...d, category: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="">— Select —</option>
                  {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: 5 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#777', marginBottom: 1 }}>Paid Schedule</div>
                <select value={newDraft.paidScheduleType} onChange={e => setNewDraft(d => ({ ...d, paidScheduleType: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="">Free / None</option>
                  <option value="weekday_window">Weekday 7:30 AM–4:30 PM</option>
                  <option value="after_hours">After-hours / Weekend</option>
                  <option value="parkade">Parkade (daily)</option>
                  <option value="permit_only">Permit only</option>
                </select>
              </div>

              {[
                { field: 'hourlyRate', label: 'Hourly Rate' },
                { field: 'timeLimit',  label: 'Time Limit' },
                { field: 'dailyMax',   label: 'Daily Max' },
                { field: 'zone',       label: 'Zone #' },
                { field: 'payLink',    label: 'Pay Link URL' },
              ].map(({ field, label }) => (
                <div key={field} style={{ marginBottom: 5 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#777', marginBottom: 1 }}>{label}</div>
                  <input type="text" value={newDraft[field]}
                    onChange={e => setNewDraft(d => ({ ...d, [field]: e.target.value }))}
                    style={{ ...inputStyle }} />
                </div>
              ))}

              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#777', marginBottom: 1 }}>Description</div>
                <textarea value={newDraft.description} rows={2}
                  onChange={e => setNewDraft(d => ({ ...d, description: e.target.value }))}
                  style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              <button onClick={handleAddEntry} disabled={!newDraft.name.trim()}
                style={{ width: '100%', fontSize: 12, fontWeight: 700, padding: '6px 0', borderRadius: 5, border: 'none', background: newDraft.name.trim() ? '#2e7d32' : '#ccc', color: '#fff', cursor: newDraft.name.trim() ? 'pointer' : 'default' }}>
                Add Lot
              </button>
            </div>
          )}

          {/* Flat lot list */}
          {!testMode && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: 20, fontSize: 12, color: '#aaa', textAlign: 'center' }}>Loading…</div>
              ) : parkingLots.length === 0 ? (
                <div style={{ padding: '24px 14px', textAlign: 'center' }}>
                  <p style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>No lots yet.</p>
                  <p style={{ fontSize: 11, color: '#bbb' }}>Use + New Lot to get started.</p>
                </div>
              ) : (
                parkingLots.map(entry => {
                  const hasPolygon = entry.polygon && entry.polygon.length >= 3;
                  const hasExits   = entry.exits && entry.exits.length > 0;
                  const isSelected = selectedEntry === entry.name;
                  return (
                    <button key={entry.name} onClick={() => selectEntry(entry.name)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 14px 9px 16px', background: isSelected ? '#fff8e6' : 'none', border: 'none', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', textAlign: 'left', borderLeft: isSelected ? '3px solid #f4a900' : '3px solid transparent' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                        <div style={{ width: 7, height: 7, borderRadius: 2, background: hasPolygon ? '#34a853' : '#e53935' }} title={hasPolygon ? 'Polygon drawn' : 'No polygon'} />
                        <div style={{ width: 7, height: 4, borderRadius: 1, background: hasExits ? '#ff5722' : 'transparent' }} title={hasExits ? `${entry.exits.length} exits` : ''} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: isSelected ? 600 : 400, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name}</div>
                        <div style={{ fontSize: 9, color: '#bbb', marginTop: 1 }}>
                          {entry.category && <span style={{ color: '#94a3b8' }}>{entry.category} · </span>}
                          {hasPolygon ? `${entry.polygon.length} pts` : <span style={{ color: '#e53935' }}>no polygon</span>}
                          {hasExits ? ` · ${entry.exits.length} exit${entry.exits.length !== 1 ? 's' : ''}` : ''}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}

          {/* Edit panel */}
          {!testMode && selectedEntry && (() => {
            const entry = parkingLots.find(e => e.name === selectedEntry);
            if (!entry) return null;
            const hasPolygon = entry.polygon && entry.polygon.length >= 3;
            const hasExits   = entry.exits && entry.exits.length > 0;
            return (
              <div style={{ borderTop: '2px solid #f4a900', background: '#fffdf5', flexShrink: 0 }}>
                <div style={{ padding: '8px 14px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#b45309', letterSpacing: '0.08em' }}>EDITING LOT</div>
                  <button onClick={handleDeselect} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', color: '#888', cursor: 'pointer' }}>✕</button>
                </div>

                <div style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>

                  {/* Rename */}
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', marginBottom: 2 }}>NAME</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input type="text" value={entryNameDraft}
                        onChange={e => setEntryNameDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRenameEntry(selectedEntry, entryNameDraft); }}
                        style={{ ...inputStyle, flex: 1 }} />
                      <button onClick={() => handleRenameEntry(selectedEntry, entryNameDraft)}
                        disabled={!entryNameDraft.trim() || entryNameDraft.trim() === selectedEntry}
                        style={{ fontSize: 10, padding: '4px 8px', borderRadius: 4, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', opacity: (!entryNameDraft.trim() || entryNameDraft.trim() === selectedEntry) ? 0.4 : 1 }}>
                        Rename
                      </button>
                    </div>
                  </div>

                  {/* Status badges */}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <div style={{ flex: 1, padding: '4px 8px', borderRadius: 5, border: `1px solid ${hasPolygon ? '#c8e6c9' : '#ffcdd2'}`, background: hasPolygon ? '#f1f8e9' : '#fff3f0', fontSize: 10, color: hasPolygon ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                      {hasPolygon ? `✓ ${entry.polygon.length} pts` : '✗ No polygon'}
                    </div>
                    <div style={{ flex: 1, padding: '4px 8px', borderRadius: 5, border: '1px solid #e0e0e0', background: hasExits ? '#fff8f0' : '#fafafa', fontSize: 10, color: hasExits ? '#e65100' : '#bbb', fontWeight: 600 }}>
                      {hasExits ? `🚪 ${entry.exits.length} exit${entry.exits.length !== 1 ? 's' : ''}` : '— No exits'}
                    </div>
                  </div>

                  {/* Polygon / exit actions */}
                  <div style={{ display: 'flex', gap: 5 }}>
                    <button onClick={handleClearPolygon} disabled={!hasPolygon}
                      style={{ flex: 1, fontSize: 10, padding: '5px 0', borderRadius: 4, border: '1px solid #ffcdd2', background: hasPolygon ? '#fff3f0' : '#fafafa', color: hasPolygon ? '#c62828' : '#ccc', cursor: hasPolygon ? 'pointer' : 'default' }}>
                      ↺ Redraw
                    </button>
                    <button onClick={handleClearExits} disabled={!hasExits}
                      style={{ flex: 1, fontSize: 10, padding: '5px 0', borderRadius: 4, border: '1px solid #e0e0e0', background: hasExits ? '#fff8f0' : '#fafafa', color: hasExits ? '#bf360c' : '#ccc', cursor: hasExits ? 'pointer' : 'default' }}>
                      ✕ Clear Exits
                    </button>
                  </div>

                  {/* Category */}
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', marginBottom: 2 }}>CATEGORY</div>
                    <select value={entry.category || ''}
                      onChange={e => handleUpdateField(selectedEntry, 'category', e.target.value)}
                      style={{ ...inputStyle, cursor: 'pointer' }}>
                      <option value="">— Select —</option>
                      {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  {/* Paid schedule */}
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', marginBottom: 2 }}>PAID SCHEDULE</div>
                    <select value={entry.paidSchedule?.type || ''}
                      onChange={e => handleUpdatePaidSchedule(selectedEntry, e.target.value || null)}
                      style={{ ...inputStyle, cursor: 'pointer' }}>
                      <option value="">Free / No schedule</option>
                      <option value="weekday_window">Weekday 7:30 AM–4:30 PM</option>
                      <option value="after_hours">After-hours / Weekend</option>
                      <option value="parkade">Parkade (daily)</option>
                      <option value="permit_only">Permit only</option>
                    </select>
                  </div>

                  {/* Other fields */}
                  {[
                    { field: 'hourlyRate', label: 'Hourly Rate' },
                    { field: 'timeLimit',  label: 'Time Limit' },
                    { field: 'dailyMax',   label: 'Daily Max' },
                    { field: 'zone',       label: 'Zone #' },
                    { field: 'payLink',    label: 'Pay Link' },
                  ].map(({ field, label }) => (
                    <div key={field}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', marginBottom: 2 }}>{label}</div>
                      <input type="text" value={entry[field] || ''}
                        onChange={e => handleUpdateField(selectedEntry, field, e.target.value)}
                        style={inputStyle} />
                    </div>
                  ))}

                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', marginBottom: 2 }}>Description</div>
                    <textarea value={entry.description || ''} rows={2}
                      onChange={e => handleUpdateField(selectedEntry, 'description', e.target.value)}
                      style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>

                  <button onClick={() => handleDeleteEntry(selectedEntry)}
                    style={{ width: '100%', fontSize: 11, fontWeight: 600, padding: '6px 0', borderRadius: 5, border: '1px solid #ffcdd2', background: '#fff3f0', color: '#c62828', cursor: 'pointer', marginTop: 2 }}>
                    🗑 Delete Lot
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Map + floating toolbar */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

          {/* Draw toolbar */}
          {!testMode && drawActive && selectedEntry && (() => {
            const entry       = parkingLots.find(e => e.name === selectedEntry);
            const hasPoly     = entry?.polygon && entry.polygon.length >= 3;
            return (
              <div style={{ position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(20,20,20,0.92)', borderRadius: 10, padding: '9px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.35)', zIndex: 10, flexWrap: 'wrap', maxWidth: '90vw' }}>
                <span style={{ fontSize: 12, color: '#f4a900', fontWeight: 600 }}>🅿 {selectedEntry}</span>
                <span style={{ fontSize: 11, color: '#ccc' }}>
                  {parkingSubMode === 'polygon'
                    ? (parkingPointCount === 0 && hasPoly ? 'polygon saved ✓' : `${parkingPointCount} pt${parkingPointCount !== 1 ? 's' : ''}${parkingPointCount >= 3 ? ' · click ● to close' : ''}`)
                    : `${exitPointCount} exit${exitPointCount !== 1 ? 's' : ''}`}
                </span>

                <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.2)' }} />

                <button
                  onClick={() => { const n = parkingSubMode === 'exits' ? 'polygon' : 'exits'; setParkingSubMode(n); parkingSubModeRef.current = n; }}
                  style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 5, border: `1px solid ${parkingSubMode === 'exits' ? '#ff5722' : 'rgba(255,255,255,0.25)'}`, background: parkingSubMode === 'exits' ? 'rgba(255,87,34,0.25)' : 'rgba(255,255,255,0.08)', color: parkingSubMode === 'exits' ? '#ff8a65' : '#fff', cursor: 'pointer' }}>
                  🚪 {parkingSubMode === 'exits' ? 'Add Exits (on)' : 'Add Exits'}
                </button>

                {parkingSubMode === 'polygon' && (
                  <>
                    {hasPoly && parkingPointCount === 0 && (
                      <button onClick={handleClearPolygon}
                        style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: '1px solid rgba(251,191,36,0.5)', background: 'rgba(251,191,36,0.15)', color: '#fbbf24', cursor: 'pointer' }}>↺ Redraw</button>
                    )}
                    <button onClick={handleParkingUndo} disabled={parkingPointCount === 0}
                      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: parkingPointCount === 0 ? 'default' : 'pointer', opacity: parkingPointCount === 0 ? 0.35 : 1 }}>↩ Undo</button>
                    <button onClick={handleParkingClose} disabled={parkingPointCount < 3}
                      style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 5, border: 'none', background: parkingPointCount >= 3 ? '#34a853' : 'rgba(255,255,255,0.08)', color: '#fff', cursor: parkingPointCount >= 3 ? 'pointer' : 'default', opacity: parkingPointCount < 3 ? 0.35 : 1 }}>✓ Save Polygon</button>
                    <button onClick={() => { clearParkingDraw(); parkingDrawPointsRef.current = []; setParkingPointCount(0); }} disabled={parkingPointCount === 0}
                      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: '#ddd', cursor: parkingPointCount === 0 ? 'default' : 'pointer', opacity: parkingPointCount === 0 ? 0.35 : 1 }}>✕ Clear</button>
                  </>
                )}

                {parkingSubMode === 'exits' && (
                  <>
                    {exitPointCount > 0 && <button onClick={handleUndoExit} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer' }}>↩ Undo</button>}
                    {exitPointCount > 0 && <button onClick={handleClearExits} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: '1px solid rgba(255,87,34,0.4)', background: 'rgba(255,87,34,0.12)', color: '#ff8a65', cursor: 'pointer' }}>✕ Clear All</button>}
                  </>
                )}
              </div>
            );
          })()}

          {/* No selection hint */}
          {!testMode && !drawActive && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', textAlign: 'center' }}>
              <div style={{ background: 'rgba(0,0,0,0.55)', borderRadius: 10, padding: '14px 22px', color: '#fff' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Select a lot from the sidebar</div>
                <div style={{ fontSize: 12, color: '#bbb' }}>or create one with + New Lot</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Test mode parking info popup */}
      {testInfoData && (() => {
        const entry       = testInfoData;
        const paid        = isPaidNow(entry.paidSchedule);
        const isPermit    = entry.paidSchedule?.type === 'permit_only';
        const isDropOff   = (entry.category || '').toLowerCase().includes('drop');
        return (
          <div onClick={() => setTestInfoData(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1500 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', width: 420, maxWidth: '92vw', boxShadow: '0 10px 40px rgba(0,0,0,0.28)', fontFamily: 'system-ui,-apple-system,sans-serif' }}>

              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '18px 20px 14px', background: '#fff8e6', borderBottom: '2px solid #f0d060' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>{entry.name}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{entry.category}</div>
                </div>
                <button onClick={() => setTestInfoData(null)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#aaa', cursor: 'pointer', lineHeight: 1, padding: 0, marginLeft: 12 }}>✕</button>
              </div>

              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>

                {isDropOff ? (
                  <div style={{ background: '#e0f2f1', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#00695c', fontWeight: 600 }}>
                    🚗 Short-term drop off / pick up only
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#bbb', marginBottom: 4 }}>APPLIED HOURS</div>
                      <div style={{ fontSize: 13, color: '#333', lineHeight: 1.5 }}>{formatSchedule(entry.paidSchedule)}</div>
                    </div>
                    {!isPermit && (
                      <div style={{ flexShrink: 0, padding: '5px 13px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: paid ? '#fdecea' : '#e6f4ea', color: paid ? '#c62828' : '#2e7d32', border: `1px solid ${paid ? '#f5c6cb' : '#b7dfbd'}` }}>
                        {paid ? 'PAYING NOW' : 'FREE NOW'}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ borderTop: '1px solid #f0f0f0' }} />

                <div style={{ display: 'flex', gap: 0 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#bbb', marginBottom: 4 }}>HOURLY RATE</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#222', whiteSpace: 'pre-line' }}>{entry.hourlyRate || '—'}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#bbb', marginBottom: 4 }}>DAILY MAX</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>{entry.dailyMax || 'No Max'}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#bbb', marginBottom: 4 }}>TIME LIMIT</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>{entry.timeLimit || 'No Limit'}</div>
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #f0f0f0' }} />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#f9f6ee', borderRadius: 10, border: '1px solid #e8d88a' }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#bbb', marginBottom: 3 }}>HONK ZONE #</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#222' }}>{entry.zone || 'N/A'}</div>
                  </div>
                  {entry.payLink && (
                    <a href={entry.payLink} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 13, fontWeight: 700, color: '#fff', background: '#f4a900', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', textDecoration: 'none' }}>
                      Pay Now →
                    </a>
                  )}
                </div>

                {entry.description && (
                  <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, padding: '10px 14px', background: '#f7f7f7', borderRadius: 8 }}>{entry.description}</div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
